import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { loginObservable } from '../common/observable.js';
import { InitState, LocalStorageText } from '../contants/common.js';
import GuardedRoute from '../pages/authen/GuardedRoute.js';
import AppLayout from '../layouts/AppLayout';
import SaleLayout from '../layouts/sale/SaleLayout';
import MotoLayout from '../layouts/MotoAuthLayout';
import MotoDefaultLayout from '../layouts/MotoDefaultLayout';
import VatChatOtoLayout from '../pages/insurance-car/Layout';
import InspectionLibertyLayout from '../layouts/InspectionLibertyLayout';
import HealthInsuranceLayout from '../layouts/HealthInsuranceLayout';
import CommonLayout from '../layouts/CommonLayout.js';

// Nhóm các lazy load theo từng module/feature để tối ưu code splitting
// Home pages
const HomePage = lazy(() => import(/* webpackChunkName: "home" */ '../pages/home/HomePage'));
const HomeCustomerPage = lazy(() =>
  import(/* webpackChunkName: "home" */ '../pages/home/HomeCustomerPage')
);
const HomeSalePage = lazy(() =>
  import(/* webpackChunkName: "home" */ '../pages/home/HomeSalePage.js')
);

// Claim pages
const ClaimPage = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimPage')
);
const ClaimDetail = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimDetail.js')
);
const ClaimInfo = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimInfo.js')
);
const ClaimInfoMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimInfoMomo.js')
);
const ClaimInfo2WheelMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimInfo2WheelMomo.js')
);
const ClaimDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/ClaimDetailMomo.js')
);
const Claim2WheelDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/Claim2WheelDetailMomo.js')
);
const ClaimGuide = lazy(() =>
  import(/* webpackChunkName: "claim-base" */ '../pages/claim/ClaimGuide.js')
);
const CreateClaimPage = lazy(() =>
  import(/* webpackChunkName: "claim-create" */ '../pages/claim/CreateClaim.js')
);
const CreateClaimMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/CreateClaimMomo.js')
);
const CreateClaim2WheelMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/CreateClaim2WheelMomo.js')
);

// Contract pages
const ContractPage = lazy(() =>
  import(/* webpackChunkName: "contract-base" */ '../pages/contract/ContractPage')
);
const ContractDetail = lazy(() =>
  import(/* webpackChunkName: "contract-base" */ 'components/Contract/Detail.js')
);

// Account pages
const AccountPage = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/AccountPage')
);
const ConfirmEmail = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/ConfirmEmail')
);
const VerifyEmail = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/account/VerifyEmail')
);
const UserInfo = lazy(() => import(/* webpackChunkName: "account" */ '../pages/account/UserInfo'));
const Password = lazy(() => import(/* webpackChunkName: "account" */ '../pages/account/Password'));
const NotificationPage = lazy(() =>
  import(/* webpackChunkName: "account" */ '../pages/notification/NotificationPage')
);

// Auth pages
const LoginPage = lazy(() => import(/* webpackChunkName: "auth" */ '../pages/login/LoginPage.js'));
const ForgotPasswordPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/forgot-password/ForgotPasswordPage.js')
);
const ChangePasswordPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/change-password/ChangePasswordPage.js')
);
const ErrorPage = lazy(() => import(/* webpackChunkName: "common" */ '../pages/ErrorPage'));
const RegisterPage = lazy(() =>
  import(/* webpackChunkName: "auth" */ '../pages/register/RegisterPage')
);

// Terms pages
const BenefitsPage = lazy(() => import(/* webpackChunkName: "terms" */ '../pages/benefits'));
const TermPage = lazy(() => import(/* webpackChunkName: "terms" */ '../pages/term/TermPage'));
const AccountTermPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/AccountTermPage')
);
const ClaimTermPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/ClaimTermPage')
);
const PrivacyPolicyPage = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/PrivacyPolicyPage')
);
const ClaimSecurity = lazy(() =>
  import(/* webpackChunkName: "terms" */ '../pages/term/ClaimSecurityPage')
);

// EWI claim pages
const CreateClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/CreatePage.js')
);
const DetailClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/DetailPage.js')
);
const InfoClaimEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/InfoPage.js')
);
const UploadLicenseEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/UploadLicensePage.js')
);
const ContractDetailEwi = lazy(() =>
  import(/* webpackChunkName: "ewi-claim" */ '../pages/ewi-claim/ContractDetailPage.js')
);

// Device authentication pages
const ShortLinkPage = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/inspection-liberty/ShortLinkPage')
);
const QnA = lazy(() => import(/* webpackChunkName: "common" */ '../pages/qna/QnA'));
const CheckAuthen = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/CheckAuthen')
);
const TutorialDevice = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/TutorialDevice')
);
const ChooseMethod = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/ChooseMethod')
);
const DeviceQrCode = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/DeviceQrCode')
);
const DeviceEmail = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/DeviceEmail')
);
const TakeDevicePhoto = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/TakeDevicePhoto')
);
const InspectSuccess = lazy(() =>
  import(/* webpackChunkName: "device-auth" */ '../pages/device-authen/Success')
);

// Take photo device authentication pages
const CheckAuthenTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/CheckAuthen'
  )
);
const ChooseMethodTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/ChooseMethod'
  )
);
const DeviceQrCodeTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/DeviceQrCode'
  )
);
const DeviceEmailTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/DeviceEmail'
  )
);
const TakeDevicePhotoTakePhoto = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/TakeDevicePhoto'
  )
);
const InspectSuccessTakePhoto = lazy(() =>
  import(/* webpackChunkName: "device-auth-photo" */ '../pages/device-authen-take-photo/Success')
);

// Sale moto pages
const LayoutMoto = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Layout')
);
const PlateNoMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/PlateNo')
);
const ConfirmPlateNoMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/ConfirmPlateNo')
);
const PaymentMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Payment')
);
const Contract2WheelDetailMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Contract2WheelDetail')
);
const SuccessMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Success')
);
const ErrorMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/Error')
);
const DrawerMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-moto" */ '../pages/sale-compulsory-moto/drawer')
);

// Sale oto pages
const LayoutOTo = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Layout')
);
const PlateNoOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/PlateNo')
);
const ConfirmPlateNoOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/ConfirmPlateNo')
);
const PaymentOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Payment')
);
const Contract4WheelDetailMotoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Contract4WheelDetail.js')
);
const SuccessOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Success')
);
const ErrorOtoPage = lazy(() =>
  import(/* webpackChunkName: "sale-oto" */ '../pages/sale-compulsory-oto/Error')
);

// Car inspection pages
const InspectMoto = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Inspect.js')
);
const MotoAuthInformation = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Information.js')
);
const MotoAuthExpertise = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Expertise.js')
);
const MotoAuthResultMessage = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/ResultMessage.js')
);
const MotoAuthTerm = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/Term.js')
);
const ViewAuthExpertise = lazy(() =>
  import(/* webpackChunkName: "inspection-moto" */ '../pages/inspection-momo/ViewExpertise.js')
);

// Liberty inspection pages
const InspectionLiberty = lazy(() =>
  import(/* webpackChunkName: "inspection-liberty" */ '../pages/inspection-liberty/Expertise')
);
const InspectionGic = lazy(() =>
  import(/* webpackChunkName: "inspection-liberty" */ '../pages/inspection-gic')
);

// GCN inspection pages
const InspectionGCN = lazy(() =>
  import(/* webpackChunkName: "inspection-gcn" */ '../pages/inspection-gcn')
);

// Insurance car pages
const InsuranceCarInputInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InputInformation')
);
const InsuranceCarChooseBenefits = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/ChooseBenefits')
);
const InsuranceCarExpertise = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/Expertise')
);
const InsuranceCarInputInformationInsurance = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InputInformationInsurance')
);
const InsuranceCarReviewInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/ReviewInformation')
);
const InsuranceCarPaymentResult = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/PaymentResult')
);
const InsuranceCarInsuranceList = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/InsuranceList')
);
const InsuranceCarDetailInformation = lazy(() =>
  import(/* webpackChunkName: "insurance-car" */ '../pages/insurance-car/DetailInformationView')
);

// Device authentication TIKI pages
const TakeDevicePhotoTiki = lazy(() =>
  import(/* webpackChunkName: "device-auth-tiki" */ '../pages/device-authen-tiki/TakeDevicePhoto')
);

// Device authentication MOMO pages
const TakeDevicePhotoMomo = lazy(() =>
  import(/* webpackChunkName: "device-auth-momo" */ '../pages/device-authen-momo/TakeDevicePhoto')
);
const TakeDevicePhotoV3 = lazy(() =>
  import(
    /* webpackChunkName: "device-auth-momo" */ '../pages/device-authen-momo/TakeDevicePhotov3.js'
  )
);

// ADLD pages
const ContractADLDPage = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/contract/ContractADLDPage')
);
const ContractADLDDetail = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/contract/ContractADLDDetail')
);
const CreateClaimADLDPage = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/CreateADLDClaim.js')
);
const ClaimDetailADLD = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/ClaimDetailADLD.js')
);
const ClaimInfoADLD = lazy(() =>
  import(/* webpackChunkName: "adld" */ '../pages/claim/ClaimInfoADLD.js')
);

// Thiết bị điện tử pages
const ContractGADDetail = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/contract/ContractGADDetail')
);
const CreateGADClaimPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/CreateGADClaim.js')
);
const ClaimDetailGAD = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/ClaimDetailGAD.js')
);
const ClaimGADInfoPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/ClaimGADInfo.js')
);
const UploadLicenseGADPage = lazy(() =>
  import(/* webpackChunkName: "gad" */ '../pages/claim/UploadLicenseGAD.js')
);

// GAD AES pages
const CreateGADClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/CreateGADClaimAES')
);
const ClaimDetailGADPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/ClaimDetailGADAES')
);
const ClaimGADInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/ClaimGADInfoAES')
);
const UploadLicenseAES = lazy(() =>
  import(/* webpackChunkName: "gad-aes" */ '../pages/claim/UploadLicenseGADAES')
);

// AMP pages
const CreateAMPClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/CreateClaim')
);
const ClaimDetailAMPPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/DetailClaim')
);
const ClaimAMPInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/InfoClaim')
);
const ClaimAMPPageAES = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/index')
);
const UploadLicenseAmp = lazy(() =>
  import(/* webpackChunkName: "amp-aes" */ '../pages/amp-claim/UploadLicense')
);
const ClaimDetailAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Detail.js')
);
const ClaimCreateAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Create.js')
);
const ClaimInfoAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/Info.js')
);
const ClaimUploadLicenseAMPPage = lazy(() =>
  import(/* webpackChunkName: "amp" */ '../pages/claim/amp/UploadLicense.js')
);

// EWI AES pages
const ClaimEWIPageAES = lazy(() =>
  import(/* webpackChunkName: "ewi-aes" */ '../pages/ewi-claim/index')
);

// PCS VPBank pages
const CreatePCSClaimPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/CreateClaim')
);
const ClaimDetailPCSPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/DetailClaim')
);
const ClaimPCSInfoPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/InfoClaim')
);
const ClaimPCSPageAES = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/index')
);
const UploadLicensePCS = lazy(() =>
  import(/* webpackChunkName: "pcs-aes" */ '../pages/pcs-claim/UploadLicense')
);

// Chi tiết bảo hiểm xe máy
const Contract2WheelDetail = lazy(() =>
  import(/* webpackChunkName: "2wheel" */ '../pages/contract/Contract2WheelDetail')
);

// Chi tiết yêu cầu bồi thường
const Claim2WheelDetail = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/Claim2WheelDetail.js')
);
const ClaimInfo2Wheel = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/ClaimInfo2Wheel.js')
);
const VerifySMS = lazy(() =>
  import(/* webpackChunkName: "claim-common" */ '../pages/claim/VerifySMS')
);
const VerifySMSMomo = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/VerifySMSMomo')
);

// Tạo bồi thường xe máy
const CreateClaim2Wheel = lazy(() =>
  import(/* webpackChunkName: "2wheel-claim" */ '../pages/claim/CreateClaim2Wheel.js')
);
const UploadLicense = lazy(() =>
  import(/* webpackChunkName: "claim-common" */ '../pages/claim/UploadLicense.js')
);
const UploadLicenseMomoPage = lazy(() =>
  import(/* webpackChunkName: "claim-momo" */ '../pages/claim/UploadLicenseMomo')
);

// Chi tiết bảo hiểm oto
const Contract4WheelDetail = lazy(() =>
  import(
    /* webpackChunkName: "4wheel" */ '../pages/contract/Contract4Wheel/Contract4WheelDetail.js'
  )
);
const Contract4WheelAutoCareDetail = lazy(() =>
  import(
    /* webpackChunkName: "4wheel" */ '../pages/contract/Contract4Wheel/Contract4WheelAutoCareDetail.js'
  )
);

// Tạo bồi thường oto
const CreateClaim4Wheel = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/CreateClaim4Wheel')
);

// Chi tiết YCBT ô tô
const Claim4WheelDetail = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/Claim4WheelDetail.js')
);
const ClaimInfo4Wheel = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/ClaimInfo4Wheel.js')
);
const UploadLicense4WheelPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-claim" */ '../pages/claim/4Wheel/UploadLicense.js')
);

// Momo AES
const Claim4WheelDetailMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelDetailMomo.js')
);
const Claim4WheelInfoMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelInfoMomo.js')
);
const Claim4WheelUploadLicenseMomoPage = lazy(() =>
  import(
    /* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Claim4WheelUploadLicenseMomo.js'
  )
);
const Create4WheelClaimMomoPage = lazy(() =>
  import(/* webpackChunkName: "4wheel-momo" */ '../pages/claim/4Wheel/Create4WheelClaimMomo.js')
);

// Health insurance pages
const HealthInsuranceGetRefundInfo = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/GetRefundInfo')
);
const HealthInsurancePayment = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment')
);
const HealthInsurancePaymentResult = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment/Result.js')
);
const HealthInsurancSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/index.js'
  )
);
const HealthInsuranceCreateSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/Create.js'
  )
);
const HealthInsuranceDetailSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/Detail.js'
  )
);
const HealthInsuranceAddInfoSupport = lazy(() =>
  import(
    /* webpackChunkName: "health-insurance" */ '../pages/health-insurance/support-request/AddInfo.js'
  )
);
const HealthInsuranceDetailEmed = lazy(() =>
  import(/* webpackChunkName: "health-insurance" */ '../pages/health-insurance/payment/Detail')
);

const Routes = () => {
  const token = localStorage.getItem(LocalStorageText.Token);
  const [loginSuccess, setLoginSuccess] = useState(token && token !== InitState);

  useEffect(() => {
    loginObservable.subscribe((token) => {
      setLoginSuccess(token && token !== InitState);
    });
  });

  return (
    <Suspense fallback={<div />}>
      <Switch>
        <Route path={['/information-car', '/expertise-car', '/term-car']}>
          <MotoLayout>
            <Switch>
              <Route path="/term-car" component={MotoAuthTerm} />
            </Switch>
          </MotoLayout>
        </Route>

        <Route path={['/inspect-car', '/result-car', '/view-inspect-car']}>
          <MotoDefaultLayout>
            <Switch>
              <Route path="/inspect-car/:codeSnippet" component={MotoAuthExpertise} />
              <Route path="/result-car/:codeSnippet" component={MotoAuthResultMessage} />
              <Route path="/view-inspect-car/:codeSnippet" component={ViewAuthExpertise} />
            </Switch>
          </MotoDefaultLayout>
        </Route>
        <Route
          path={[
            '/health-insurance-get-refund-info',
            '/social-insurance/medical-insurance',
            '/social-insurance/detail',
            '/inspect-car-coi',
            '/health-ins/payment',
            '/health-insurance-support',
            '/health-insurance-support-create',
            '/health-insurance-support-detail',
            '/health-insurance-support-add-info',
          ]}
        >
          <HealthInsuranceLayout>
            <Switch>
              <Route
                exact
                path="/health-insurance-get-refund-info/:codeSnippet"
                component={HealthInsuranceGetRefundInfo}
              />
              <Route
                exact
                path="/social-insurance/medical-insurance/:codeSnippet"
                component={HealthInsurancePayment}
              />
              <Route
                exact
                path="/social-insurance/detail/:codeSnippet"
                component={HealthInsuranceDetailEmed}
              />
              <Route exact path="/health-ins/payment" component={HealthInsurancePaymentResult} />
              <Route path="/inspect-car-coi/:codeSnippet" component={InspectionGCN} />
              <Route
                path="/health-insurance-support/:codeSnippet"
                component={HealthInsurancSupport}
              />
              <Route
                path="/health-insurance-support-create/:codeSnippet"
                component={HealthInsuranceCreateSupport}
              />
              <Route
                path="/health-insurance-support-detail/:codeSnippet"
                component={HealthInsuranceDetailSupport}
              />
              <Route
                path="/health-insurance-support-add-info/:codeSnippet"
                component={HealthInsuranceAddInfoSupport}
              />
            </Switch>
          </HealthInsuranceLayout>
        </Route>
        <Route path={['/c', '/a', '/test-cam']}>
          <CommonLayout>
            <Switch>
              <Route path="/c/:codeSnippet" component={InspectionGic} />
              <Route path="/a/:codeSnippet" component={InspectionGic} />
            </Switch>
          </CommonLayout>
        </Route>
        <Route path={['/liv-inspect-car', '/gic-inspect-car', '/g']}>
          <InspectionLibertyLayout>
            <Switch>
              <Route path="/liv-inspect-car/:codeSnippet" component={InspectionLiberty} />
              <Route path="/gic-inspect-car/:codeSnippet" component={InspectionLiberty} />
              <Route path="/g/:slug" component={ShortLinkPage} />
            </Switch>
          </InspectionLibertyLayout>
        </Route>
        <Route path={['/insurance-car']}>
          <VatChatOtoLayout>
            <Switch>
              <Route
                path="/insurance-car/input-informations/:partnerId"
                component={InsuranceCarInputInformation}
              />
              <Route
                path="/insurance-car/choose-benefits/:codeSnippet"
                component={InsuranceCarChooseBenefits}
              />
              <Route
                path="/insurance-car/expertise/:codeSnippet"
                component={InsuranceCarExpertise}
              />
              <Route
                path="/insurance-car/input-informations-insurance/:codeSnippet"
                component={InsuranceCarInputInformationInsurance}
              />
              <Route
                path="/insurance-car/review-information/:codeSnippet"
                component={InsuranceCarReviewInformation}
              />
              <Route
                path="/insurance-car/payment-result/:codeSnippet"
                component={InsuranceCarPaymentResult}
              />
              <Route
                path="/insurance-car/order-detail/:codeSnippet"
                component={InsuranceCarDetailInformation}
              />
            </Switch>
          </VatChatOtoLayout>
        </Route>
        <Route path={['/sale']}>
          <SaleLayout>
            <Switch>
              <Route exact path="/sale" component={HomeSalePage} />
              <GuardedRoute
                exact
                path="/sale/account"
                component={AccountPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/sale/user-info"
                component={UserInfo}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/sale/password"
                component={Password}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                path="/sale/order"
                component={InsuranceCarInsuranceList}
                isAuth={loginSuccess}
              />
              <Route exact path="/sale/account-term" component={AccountTermPage} />
            </Switch>
          </SaleLayout>
        </Route>
        <Route path={['/compulsory-moto']}>
          <LayoutMoto>
            <Switch>
              <Route path="/compulsory-moto/plate-no/:partnerId" component={PlateNoMotoPage} />
              <Route
                path="/compulsory-moto/confirm-plate-no/:requestId"
                component={ConfirmPlateNoMotoPage}
              />
              <Route path="/compulsory-moto/payment/:requestId" component={PaymentMotoPage} />
              <Route path="/compulsory-moto/success/:requestId" component={SuccessMotoPage} />
              <Route
                path="/compulsory-moto/contract-detail/:requestId"
                component={Contract2WheelDetailMotoPage}
              />
              <Route path="/compulsory-moto/error/:requestId" component={ErrorMotoPage} />
              <Route path="/compulsory-moto/drawer/:partnerId" component={DrawerMotoPage} />
            </Switch>
          </LayoutMoto>
        </Route>

        <Route path={['/compulsory-oto']}>
          <LayoutOTo>
            <Switch>
              <Route path="/compulsory-oto/plate-no/:partnerId" component={PlateNoOtoPage} />
              <Route
                path="/compulsory-oto/confirm-plate-no/:requestId"
                component={ConfirmPlateNoOtoPage}
              />
              <Route path="/compulsory-oto/payment/:requestId" component={PaymentOtoPage} />
              <Route path="/compulsory-oto/success/:requestId" component={SuccessOtoPage} />
              <Route
                path="/compulsory-oto/contract-detail/:requestId"
                component={Contract4WheelDetailMotoPage}
              />
              <Route path="/compulsory-oto/error/:requestId" component={ErrorOtoPage} />
            </Switch>
          </LayoutOTo>
        </Route>
        <Route>
          <AppLayout>
            <Switch>
              <Route exact path="/" component={HomePage} />
              <Route exact path="/home-customer" component={HomeCustomerPage} />
              <GuardedRoute exact path="/claim" component={ClaimPage} isAuth={loginSuccess} />
              <GuardedRoute
                exact
                path="/claim-detail-pcs/:claimId"
                component={ClaimDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-info/:claimId"
                component={ClaimInfo}
                isAuth={loginSuccess}
              />
              <Route exact path="/claim-info-momo/:polMomo" component={ClaimInfoMomoPage} />
              <Route exact path="/claim-momo/:polMomo" component={ClaimDetailMomoPage} />
              <GuardedRoute
                exact
                path="/claim-guide/:policyName"
                component={ClaimGuide}
                isAuth={loginSuccess}
              />
              <GuardedRoute exact path="/contract" component={ContractPage} isAuth={loginSuccess} />
              <GuardedRoute
                exact
                path="/contract-detail-pcs/:contractId"
                component={(props) => <ContractDetail {...props} contractType="pcs" />}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim/:contractId"
                component={CreateClaimPage}
                isAuth={loginSuccess}
              />
              <Route exact path="/create-claim-momo/:polMomo" component={CreateClaimMomoPage} />
              <GuardedRoute
                exact
                path="/contract-detail-ybb/:contractId"
                component={Contract2WheelDetail}
                isAuth={loginSuccess}
              />

              <GuardedRoute
                exact
                path="/claim-detail-ybb/:claimId"
                component={Claim2WheelDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-2-wheel-info/:claimId"
                component={ClaimInfo2Wheel}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/verify-sms/:claimId"
                component={VerifySMS}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim-2-wheel/:contractId"
                component={CreateClaim2Wheel}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/upload-license/:action/:claimId"
                component={UploadLicense}
                isAuth={loginSuccess}
              />
              <Route
                exact
                path="/claim-2-wheel-info-momo/:polMomo"
                component={ClaimInfo2WheelMomoPage}
              />
              <Route
                exact
                path="/upload-license-momo/:action/:polMomo"
                component={UploadLicenseMomoPage}
              />
              <Route
                exact
                path="/2wheel-claim-momo/:polMomo"
                component={Claim2WheelDetailMomoPage}
              />
              <Route
                exact
                path="/create-2-wheel-claim-momo/:polMomo"
                component={CreateClaim2WheelMomoPage}
              />
              <Route exact path="/verify-sms-momo/:polMomo" component={VerifySMSMomo} />
              <GuardedRoute
                exact
                path="/contract-detail-yab/:id"
                component={Contract4WheelDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/contract-detail-auto/:id"
                component={Contract4WheelAutoCareDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim-4-wheel/:contractId"
                component={CreateClaim4Wheel}
                isAuth={loginSuccess}
              />

              <GuardedRoute
                exact
                path="/claim-detail-yab/:claimId"
                component={Claim4WheelDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/upload-license-4-wheel/:action/:claimId"
                component={UploadLicense4WheelPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-4-wheel-info/:claimId"
                component={ClaimInfo4Wheel}
                isAuth={loginSuccess}
              />

              <Route
                exact
                path="/4wheel-claim-create-mono/:polMomo"
                component={Create4WheelClaimMomoPage}
              />
              <Route exact path="/4wheel-claim/:polMomo" component={Claim4WheelDetailMomoPage} />
              <Route
                exact
                path="/4wheel-claim-info-momo/:polMomo"
                component={Claim4WheelInfoMomoPage}
              />
              <Route
                exact
                path="/4wheel-claim-upload-license-momo/:action/:polMomo"
                component={Claim4WheelUploadLicenseMomoPage}
              />
              <GuardedRoute
                exact
                path="/contract-adld"
                component={ContractADLDPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/contract-detail-adld/:contractId"
                component={ContractADLDDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim-adld/:contractId"
                component={CreateClaimADLDPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-detail-adld/:claimId"
                component={ClaimDetailADLD}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-info-adld/:claimId"
                component={ClaimInfoADLD}
                isAuth={loginSuccess}
              />

              <GuardedRoute
                exact
                path="/contract-detail-epi/:contractId"
                component={ContractGADDetail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim-ep/:contractId"
                component={CreateGADClaimPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-detail-epi/:claimId"
                component={ClaimDetailGAD}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/claim-info-ep/:claimId"
                component={ClaimGADInfoPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/upload-license-ep/:action/:claimId"
                component={UploadLicenseGADPage}
                isAuth={loginSuccess}
              />

              <Route exact path="/ep-claim-create/:polNo" component={CreateGADClaimPageAES} />
              <Route exact path="/ep-claim/:polNo" component={ClaimDetailGADPageAES} />
              <Route exact path="/ep-claim-info/:polNo" component={ClaimGADInfoPageAES} />
              <Route
                exact
                path="/ep-claim-upload-license/:action/:polNo"
                component={UploadLicenseAES}
              />
              <Route exact path="/amp-claim/:codeSnippet" component={ClaimAMPPageAES} />
              <Route
                exact
                path="/amp-claim-create/:codeSnippet"
                component={CreateAMPClaimPageAES}
              />
              <Route
                exact
                path="/amp-claim-detail/:codeSnippet"
                component={ClaimDetailAMPPageAES}
              />
              <Route exact path="/amp-claim-info/:codeSnippet" component={ClaimAMPInfoPageAES} />
              <Route
                exact
                path="/amp-claim-upload-license/:action/:polNo"
                component={UploadLicenseAmp}
              />
              <GuardedRoute
                exact
                path="/claim-detail-amp/:claimId"
                component={ClaimDetailAMPPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/amp-app-claim-create/:claimId"
                component={ClaimCreateAMPPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/amp-app-claim-info/:claimId"
                component={ClaimInfoAMPPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/amp-app-claim-upload-license/:action/:polNo"
                component={ClaimUploadLicenseAMPPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/contract-detail-amp/:contractId"
                component={(props) => (
                  <ContractDetail
                    getApi="get-detail-policy-amp"
                    createPage="amp-app-claim-create"
                    {...props}
                    contractType="amp"
                  />
                )}
                isAuth={loginSuccess}
              />
              <Route exact path="/ewi-claim/:codeSnippet" component={ClaimEWIPageAES} />
              <GuardedRoute
                exact
                path="/contract-detail-ewi/:contractId"
                component={(props) => (
                  <ContractDetailEwi
                    {...props}
                    getApi="get-detail-policy-ewi"
                    createPage="create-claim-ewi"
                    contractType="ewi"
                  />
                )}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/create-claim-ewi/:claimId"
                component={CreateClaimEwi}
                isAuth={loginSuccess}
              ></GuardedRoute>
              <GuardedRoute
                exact
                path="/claim-detail-ewi/:claimId"
                component={DetailClaimEwi}
                isAuth={loginSuccess}
              ></GuardedRoute>
              <GuardedRoute
                exact
                path="/claim-info-ewi/:claimId"
                component={InfoClaimEwi}
                isAuth={loginSuccess}
              ></GuardedRoute>
              <GuardedRoute
                exact
                path="/upload-license-ewi/:action/:polNo"
                component={UploadLicenseEwi}
                isAuth={loginSuccess}
              ></GuardedRoute>
              <Route exact path="/pcs-claim/:codeSnippet" component={ClaimPCSPageAES} />
              <Route
                exact
                path="/pcs-claim-create/:codeSnippet"
                component={CreatePCSClaimPageAES}
              />
              <Route
                exact
                path="/pcs-claim-create-vpbank/:codeSnippet"
                component={lazy(() => import('../pages/pcs-claim/CreateClaimVpbank'))}
              />
              <Route
                exact
                path="/pcs-claim-detail/:codeSnippet"
                component={ClaimDetailPCSPageAES}
              />
              <Route
                exact
                path="/pcs-claim-detail-vpbank/:codeSnippet"
                component={(props) => (
                  <ClaimDetailPCSPageAES {...props} getApi="get-detail-claim-pcs" />
                )}
              />
              <Route exact path="/pcs-claim-info/:codeSnippet" component={ClaimPCSInfoPageAES} />
              <Route
                exact
                path="/pcs-claim-upload-license/:action/:polNo"
                component={UploadLicensePCS}
              />
              <GuardedRoute
                exact
                path="/confirm-email"
                component={ConfirmEmail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/verify-email"
                component={VerifyEmail}
                isAuth={loginSuccess}
              />
              <GuardedRoute exact path="/user-info" component={UserInfo} isAuth={loginSuccess} />
              <GuardedRoute exact path="/password" component={Password} isAuth={loginSuccess} />
              <GuardedRoute exact path="/account" component={AccountPage} isAuth={loginSuccess} />
              <GuardedRoute
                exact
                path="/confirm-email"
                component={ConfirmEmail}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/verify-email"
                component={VerifyEmail}
                isAuth={loginSuccess}
              />
              <GuardedRoute exact path="/user-info" component={UserInfo} isAuth={loginSuccess} />
              <GuardedRoute
                exact
                path="/notification"
                component={NotificationPage}
                isAuth={loginSuccess}
              />
              <GuardedRoute
                exact
                path="/benefits/:policyName"
                component={(props) => <BenefitsPage {...props} />}
                isAuth={loginSuccess}
              />
              <GuardedRoute exact path="/term" component={TermPage} isAuth={loginSuccess} />
              <GuardedRoute
                exact
                path="/privacy-policy"
                component={PrivacyPolicyPage}
                isAuth={loginSuccess}
              />
              <Route exact path="/claim-term" component={ClaimTermPage} />
              <Route exact path="/qna" component={QnA} />
              <Route exact path="/account-term" component={AccountTermPage} />
              <Route exact path="/chinh-sach-bao-mat-du-lieu-ca-nhan" component={ClaimSecurity} />
              <Route exact path="/check-authen" component={CheckAuthen} />
              <Route exact path="/tutorial-device" component={TutorialDevice} />
              <Route exact path="/tutorial-device/:redirectPath" component={TutorialDevice} />
              <Route exact path="/choose-method" component={ChooseMethod} />
              <Route exact path="/qr-device" component={DeviceQrCode} />
              <Route exact path="/email-device" component={DeviceEmail} />
              <Route exact path="/time-inspect-policy/:codeSnippet" component={TakeDevicePhoto} />
              <Route exact path="/inspect-success" component={InspectSuccess} />
              <Route exact path="/inspect-tiki/:codeSnippet" component={TakeDevicePhotoTiki} />
              <Route exact path="/inspect/:codeSnippet" component={TakeDevicePhotoMomo} />
              <Route exact path="/inspect-phone-v3/:codeSnippet" component={TakeDevicePhotoV3} />
              <Route exact path="/check-authen-take-photo" component={CheckAuthenTakePhoto} />
              <Route exact path="/choose-method-take-photo" component={ChooseMethodTakePhoto} />
              <Route exact path="/qr-device-take-photo" component={DeviceQrCodeTakePhoto} />
              <Route exact path="/email-device-take-photo" component={DeviceEmailTakePhoto} />
              <Route
                exact
                path="/time-inspect-policy-take-photo/:codeSnippet"
                component={TakeDevicePhotoTakePhoto}
              />
              <Route exact path="/inspect-success-take-photo" component={InspectSuccessTakePhoto} />

              <Route exact path="/login" component={LoginPage} />
              <Route exact path="/register" component={RegisterPage} />
              <Route exact path="/forgot-password" component={ForgotPasswordPage} />
              <Route
                exact
                path="/change-password/:phone/:tokenKey"
                component={ChangePasswordPage}
              />
              <Route component={(_) => <ErrorPage status={404} message="Page not found." />} />
            </Switch>
          </AppLayout>
        </Route>
      </Switch>
    </Suspense>
  );
};

export default Routes;
